import { Component, computed, input, model, OnDestroy, OnInit, Self } from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { ReplaySubject, Subscription } from 'rxjs';

import { Option } from '../../../models/option.model';

@Component({
  selector: 'eiq-select',
  templateUrl: 'select.component.html',
  styleUrls: ['select.component.scss']
})
export class SelectComponent implements OnInit, OnDestroy, ControlValueAccessor {
  readonly defaultOptionValue = { defaultOption: true };

  defaultOption = input<string>();
  hint = input<string>();
  icon = input<IconDefinition>();
  label = input<string>();
  loading = input<boolean>(false);
  multiple = input<boolean>();
  options = input<Option<unknown>[]>([]);
  placeholder = input<string>();
  showDefaultOption = input<boolean>(false);
  size = input<'sm' | 'md'>('md');
  testId = input<string>();
  enableFilter = input<boolean>(false);

  searchInput = model('');

  onChange: (value: unknown | unknown[]) => void;
  onTouched: () => void;

  control: FormControl;
  currentValue: unknown | unknown[];

  hiddenValues$ = computed(() => {
    const search = this.searchInput()?.toLowerCase();
    if (!search) {
      return new Set();
    }

    return new Set(
      this.options()
        .filter((o) => !o.text.toLowerCase().includes(search))
        .map((o) => o.value)
    );
  });

  private input$ = new ReplaySubject<unknown | unknown[]>(1);
  private subscriptions: Subscription[] = [];

  constructor(@Self() public ngControl: NgControl) {
    this.ngControl.valueAccessor = this;
  }

  ngOnInit(): void {
    this.control = this.ngControl.control as FormControl;
    this.subscriptions.push(
      this.input$.subscribe((val) => {
        this.onTouched();
        this.onChange(val);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(value: unknown | unknown[]): void {
    if (!value && !this.defaultOption()) {
      this.currentValue = null;
      return;
    }
    this.currentValue = this.mapCurrentValue(value);
  }

  setValue(change: MatSelectChange): void {
    this.currentValue = this.mapSetValue(change);
    this.input$.next(this.mapSetValue(change));
  }

  clearValue(): void {
    this.currentValue = this.mapCurrentValue(this.multiple() ? [] : null);
    this.input$.next(this.multiple() ? [] : null);
  }

  isInvalid(): boolean {
    return !!this.control?.invalid && this.control.touched;
  }

  setDisabledState?(_isDisabled: boolean): void {
    // Implement if needed
  }

  private mapSetValue(change: MatSelectChange): unknown | unknown[] {
    if (change.value === this.defaultOptionValue) {
      return null;
    }

    return change.value || this.defaultOption() ? change.value : this.multiple() ? [] : null;
  }

  private mapCurrentValue(value: unknown | unknown[]): unknown | unknown[] {
    if (!value && this.showDefaultOption() && this.defaultOption()) {
      return this.defaultOptionValue;
    }

    return value;
  }
}
